import {
  html, LitElement,
} from 'lit-element';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import AabIcon from '@aab/sc-aab-icon/src/AabIcon';
import { n200, g300 } from '@aab/sc-styles-colors';
import { sy_arrow_chevron_up as chevronUp, sy_arrow_chevron_down as chevronDown } from '@aab/sc-aab-icon-set';
import * as icons from '@aab/sc-aab-icon-set/src/lib/flags';
import Styles from './styles';

const SPACE_BAR_KEY_CODES = [0, 32];
const ENTER_KEY_CODE = 13;
const DOWN_ARROW_KEY_CODE = 40;
const UP_ARROW_KEY_CODE = 38;
const ESCAPE_KEY_CODE = 27;

export default class AabSelect extends ScopedElementsMixin(LitElement) {
  static get properties() {
    return {
      placeholder: { type: String },
      value: { type: Object, reflect: true },
      options: { type: Array },
      label: { type: String },
      showOptions: { type: Boolean },
      invalid: { type: Boolean },
      focused: { type: Boolean },
      displayItem: {
        type: String,
        attribute: 'display-item',
      },
      selectedItem: {
        type: String,
        attribute: 'selected-item',
      },
      selectAreaNoBorder: {
        type: Boolean,
        attribute: 'select-area-no-border',
      },
      selectAreaDisabled: {
        type: Boolean,
        attribute: 'select-area-disabled',
      },
      selectAreaSmall: {
        type: Boolean,
        attribute: 'select-area-small',
      },
      id: {
        type: String,
      },
    };
  }

  static get scopedElements() {
    return {
      'aab-icon': AabIcon,
    };
  }

  static get styles() {
    return Styles;
  }

  constructor() {
    super();
    this.options = [];
    this.displayItem = 'text';
    this.id = `aab-select-dropdown-${new Date().getTime()}-${this.id ? this.id : ''}`;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', (event) => {
      const path = event.path || event.composedPath();
      if (!path.filter((element) => element.id === this.id).length) {
        this.closeList();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', (event) => {
      const path = event.path || event.composedPath();
      if (!path.filter((element) => element.id === this.id).length) {
        this.closeList();
      }
    });
  }

  firstUpdated() {
    super.firstUpdated();
    // defining the shadow dom of first option and last option in list
    this.lastOptionDom = this.shadowRoot.querySelector(`#option-${this.options.length}`);
    this.firstOptionDom = this.shadowRoot.querySelector('#option-first');

    // validate input data
    if (!(this.placeholder || this.value)) { this.invalid = true; }
    // set style none to option if no slot content added from widget
    if (!this.firstOptionDom.firstElementChild.assignedNodes().length) {
      this.firstOptionDom.style.display = 'none';
    }
    if (!this.lastOptionDom.firstElementChild.assignedNodes().length) {
      this.lastOptionDom.style.display = 'none';
    }
  }

/**
 * Method will find next enabled option index (used in key event).
 * @param {number} currentIndex - Index of currently active element.
 * @returns {Number} - returns next enabled index
 */
  findNextItemIndex(currentIndex) {
    let nextIndex = currentIndex;
    if (currentIndex < this.options.length) {
      this.options.find((option, index) => {
        if (!option.disabled && index > nextIndex) {
          nextIndex = index;
          return true;
        }
        return false;
      });
    }
    return nextIndex;
  }

/**
 * Method will find previous enabled option index (used in key event).
 * @param {Number} currentIndex - Index of currently active element.
 * @returns {Number} - returns previous enabled index
 */
  findPreviousItemIndex(currentIndex) {
    let prevIndex = currentIndex;
    if (prevIndex !== 0) {
      this.options.reduceRight((value, option, index) => {
        if (!option.disabled && index < prevIndex) {
          if (prevIndex === currentIndex) {
            prevIndex = index;
          }
          return true;
        }
        return false;
      });
    }
    return prevIndex;
  }

/**
 * Method to handle case if all the items in dropdown are disabled.
 * @param {Number} index - Index of element.
 */
  setFocus(index) {
    if (index !== -1) {
      this.shadowRoot.querySelector(`#option-${index}`).focus();
    }
  }

/**
 * Method to handle Up and Down arrow key events.
 * @param {Number} direction - KeyCode of pressed key.
 */
  focusNextListItem(direction) {
    const activeElementId = this.shadowRoot.activeElement.id;
    if (activeElementId === 'aab-dropdown__selected') {
      const nextItemIndex = this.findNextItemIndex(-1);
      this.setFocus(nextItemIndex);
    } else {
      const itemIndex = activeElementId.substring(7);
      const currentIndex = itemIndex.length ? itemIndex : -1;
      const count = this.options.length;
      if (direction === DOWN_ARROW_KEY_CODE) {
        this.handleDownArrowKey(this.lastOptionDom, activeElementId, count, currentIndex);
      } else if (direction === UP_ARROW_KEY_CODE) {
        this.handleUpArrowKey(this.lastOptionDom, activeElementId, count, currentIndex);
      }
    }
  }

/**
 * Method is handling down arrow key event.
 * @param {Object} lastOptionDom - html of last option slot node.
 * @param {string} activeElementId - id for current active element.
 * @param {Number} count - options length.
 * @param {Number} currentIndex - current item index.
 */
  handleDownArrowKey(lastOptionDom, activeElementId, count, currentIndex) {
    if (lastOptionDom && activeElementId === `option-${count - 1}`) {
      this.setFocus(count);
    } else if (currentIndex < count - 1) {
      // Current Element is not last item then
      this.setFocus(this.findNextItemIndex(currentIndex));
    }
  }

/**
 * Method is handling up arrow key event.
 * @param {Object} lastOptionDom - html of last option slot node.
 * @param {string} activeElementId - id for current active element.
 * @param {Number} count - options length.
 * @param {Number} currentIndex - current item index.
 */
  handleUpArrowKey(lastOptionDom, activeElementId, count, currentIndex) {
    if (lastOptionDom && activeElementId === `option-${count}`) {
      this.setFocus(count - 1);
    } else if (currentIndex > 0) {
      // Current Element is not first item
      const nextListItemId = `option-${this.findPreviousItemIndex(currentIndex)}`;
      this.shadowRoot.querySelector(`#${nextListItemId}`).focus();
    }
  }

/**
 * Method returning boolean whether option is in selected state or not.
 * @param {any} option - Option from dropdown.
 * @returns {boolean} - returns boolean value based on the selected option
 */
  isSelected(option) {
    return (
      this.value === option
      || (this.value && this.value[this.displayItem] && this.value[this.displayItem] === option[this.displayItem])
    );
  }

/**
 * Method to close dropdown list.
 */
  closeList() {
    this.showOptions = false;
  }

/**
 * Method is handling list visibility on key and click events.
 * @param {Object} event - Event.
 */
  toggleListVisibility(event) {
    event.preventDefault();
    if (this.invalid || this.selectAreaDisabled) { return; }
    const openDropDown = SPACE_BAR_KEY_CODES.indexOf(event.keyCode) > -1 || event.keyCode === ENTER_KEY_CODE;
    if (event.keyCode === ESCAPE_KEY_CODE) {
      this.closeList();
    }
    if (event.type === 'click' || openDropDown) {
      this.showOptions = !this.showOptions;
    }
    if (event.keyCode === DOWN_ARROW_KEY_CODE) {
      this.focusNextListItem(DOWN_ARROW_KEY_CODE);
    }
    if (event.keyCode === UP_ARROW_KEY_CODE) {
      this.focusNextListItem(UP_ARROW_KEY_CODE);
    }
  }

/**
 * Method is handling when user selects and item from dropdown.
 * @param {Object} event - Event.
 * @param {number} index - selected option index.
 */
  handleItemSelect(event, index) {
    event.preventDefault();
    if (event.type === 'click') {
      this.setSelectedListItem(index);
    }
    switch (event.keyCode) {
      case ENTER_KEY_CODE:
        this.setSelectedListItem(index);
        break;
      case DOWN_ARROW_KEY_CODE:
        this.focusNextListItem(DOWN_ARROW_KEY_CODE);
        break;

      case UP_ARROW_KEY_CODE:
        this.focusNextListItem(UP_ARROW_KEY_CODE);
        break;

      case ESCAPE_KEY_CODE:
        this.closeList();
        break;

      default:
    }
  }

/**
 * Method to dispatch select event and set selected value.
 * @param {number} index - selected option index.
 */
  setSelectedListItem(index) {
    const count = this.options.length;
    let value;

    if (index === count) {
      value = 'last-option-slot';
    } else {
      this.value = this.options[index];
      value = this.value;
      this.closeList();
    }
    const eventObj = new CustomEvent('aab-select-value-selected', {
      detail: {
        value,
        index,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(eventObj);
  }

/**
 * Method is returning class name based on valid/invalid scenarios.
 * @returns {string} - returns class name of the select area border
 */
  borderClassName() {
    let className = '';
    if (this.invalid) {
      className = 'invalid';
    }
    if (this.focused && !this.invalid) {
      className = 'focused';
    }
    return className;
  }

/**
 * Method is returning selected value or icon based on key.
 * @param {Object} option - selected option.
 * @param {string} key - selected item property as key from widget.
 * @returns {(string | html )} - returns html or value from options
 */
  setSelectedProperty(option, key) {
    // if key is icon, return the icon html
    if (key === 'icon') {
      const iconName = icons[option.icon];
      // if icon name is undefined, return the value of the option
      return iconName
        ? html`<div class="selected-icon ${this.selectAreaDisabled ? 'opacity-50' : ''}">
          <aab-icon svg=${iconName} size="3" class="align-self-center d-flex"></aab-icon></div>`
        : html`<div class="align-self-center icon-font d-flex">${option.value}</div>`;
    }
    // if key not icon, then option value for the respective key is returned
    return option[key];
  }

/**
 * Method is returning selected value.
 * @returns {string} - returns selected value or placeholder
 */
  selectedOption() {
    /* if selected item is not empty then we will call setSelectedProperty to get the icon/value
     based on the value passed else it will return value based on the display item value */
    if (this.value) {
      return this.selectedItem ? this.setSelectedProperty(this.value, this.selectedItem)
        : this.value[this.displayItem] || this.value;
    }
    return this.placeholder;
  }

/**
 * Method is returning dropdown last option html.
 * @returns {html} - returns html for last option slot
 */
  getLastOption() {
    const index = this.options.length;
    return html`
    <li id='option-${index}' role="option"
      tabindex="0"
      data-index="${index}"
      @click=${(event) => this.handleItemSelect(event, index)}
      @keydown=${(event) => this.handleItemSelect(event, index)}
    >
      <slot name="last-option-slot"></slot>
    </li>
    `;
  }

/**
 * Method is returning disabled class based on property.
 * @returns {string} - returns disabled class name
 */
  setDisabledClass() {
    let disableClass = '';
    if (this.selectAreaDisabled) {
      disableClass = 'value-disabled';
      if (this.selectedItem === 'icon') {
        disableClass = 'icon-disabled';
      }
    }
    return disableClass;
  }

  render() {
    return html`
      <div class="row ${this.selectAreaSmall ? 'mx-0' : ''}">
        <div class="${this.selectAreaSmall ? '' : 'col'}">
          ${(this.label && this.label.trim())
    ? html`<div id="aab-dropdown-label" class="aab-dropdown__label">${this.label}</div>`
    : ''
}
          <div id=${this.id}>
            <ul class="aab-dropdown position-relative rounded-2 p-0
              ${this.setDisabledClass()}
              ${this.selectAreaSmall ? 'd-flex' : ''}
              ${this.selectAreaNoBorder ? 'select-area-no-border my-0' : ''}"
            >
              <li
                id="aab-dropdown__selected"
                role="listitem"
                aria-haspopup="listbox"
                aria-label=" ${(this.label && this.label.trim()) ? 'aab-dropdown-label aab-dropdown__selected' : 'aab-dropdown__selected'} "
                tabindex="0"
                class="${this.borderClassName()}
                  ${this.selectAreaDisabled ? 'disabled' : ''}
                  p-1
                  text-break
                "
                @click=${(event) => this.toggleListVisibility(event)}
                @keydown=${(event) => this.toggleListVisibility(event)}
              >
                <div class="d-flex flex-row justify-content-between">
                  <div class="align-self-center">
                    ${this.selectedOption()}
                  </div>
                  <div class="align-self-center">
                    ${this.showOptions
    ? html`<aab-icon svg="${chevronUp}" size="3" class="align-self-center d-flex"></aab-icon>`
    : html`<aab-icon
                          color="${this.selectAreaDisabled ? n200 : g300}"
                          svg=${chevronDown}
                          size="3"
                          class="align-self-center d-flex"
                        ></aab-icon>`
}
                  </div>
                </div>
              </li>
            </ul>
            <div
              class="aab-dropdown__list-container d-none
                ${this.selectAreaSmall ? ' full-width-dropdown position-absolute' : 'position-relative'}
                ${this.showOptions ? 'expand d-flex' : ''}
                "
              aria-expanded="${this.showOptions}"
              tabindex="${this.showOptions ? 0 : -1}"
            >
              <ul
                role="listbox"
                class="aab-dropdown__list my-0 shadow-dropdown overflow-auto p-0
                  ${this.selectAreaSmall ? 'position-relative' : 'position-absolute'}
                "
                tabindex="${this.showOptions ? '0' : '-1'}"
                aria-label=${(this.label && this.label.trim()) ? 'aab-dropdown-label aab-dropdown__selected' : 'aab-dropdown__selected'}
              >
                <li
                  role="option"
                  id="option-first"
                  class="text-break"
                  tabindex="0"
                >
                  <slot name="first-option-slot"></slot>
                </li>
                ${this.options.map((option, index) => html`
                  <li
                    role="option"
                    class="aab-dropdown__list-item py-1 px-2 d-flex align-items-center align-self-center text-break
                      ${option.icon ? 'text-height' : ''}
                      ${option.disabled ? 'disabled' : ''}
                      ${this.isSelected(option) ? 'selected' : ''}
                    "
                    tabindex="${this.showOptions ? 0 : -1}"
                    id="${`option-${index}`}" data-index="${index}"
                    @click=${(event) => this.handleItemSelect(event, index)}
                    @keydown=${(event) => this.handleItemSelect(event, index)}
                  >
                    ${option.icon ? html`<div class="icon-height">${this.setSelectedProperty(option, 'icon')}</div>` : ''}
                    ${option[this.displayItem] || option}
                  </li>
                `)}
                ${this.getLastOption()}
              </ul>
            </div>
          </div>
        </div>
      </div>`;
  }
}
